import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Picture,
  RenderedMarkerPLPPDP,
  useServicesContext,
  VehicleBasic,
} from '@oneaudi/vtp-shared';

import { IconButton } from '@oneaudi/unified-web-components';
import { GfaLayerManagerV1, LayerHandleV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';

import {
  GalleryContainer,
  ImageWrapper,
  ButtonContainer,
  NavButtonContainer,
  MobileNavButtonContainer,
} from '../../styles/SwipePicture.styles';
import FullScreenPicture, { SlideImage } from './FullScreenPicture';
import { AvpWebstreamButton } from '../AvpWebstreamIntegration/AvpWebstreamButton';
import { IconNamesType } from '@oneaudi/unified-web-components/dist/cjs/components/icon/icon-names.generated';
import { trackOpenGallery, trackCloseGallery, trackImageChange } from '../../../tracking/Tracking';
import { useI18n } from '@oneaudi/i18n-context';
import { SYS_SIZE_FIXED_200, SYS_SIZE_FIXED_300 } from '@oneaudi/unified-web-common';

interface SwipeStageProps {
  pictures: Picture[];
  vehicle: VehicleBasic;
}

const SwipePicture = ({ pictures, vehicle }: SwipeStageProps) => {
  const layerManager = useServicesContext().getAdditionalService(
    'gfa:layer-manager'
  ) as GfaLayerManagerV1;

  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;

  const ariaLabelExpandButton = useI18n({
    defaultMessage: 'Resize image',
    id: 'stockcars.stage.button.expand.arialabel',
  });
  const ariaLabelPreviousButton = useI18n({
    defaultMessage: 'Navigate to previous image',
    id: 'stockcars.stage.button.previous.arialabel',
  });
  const ariaLabelNextButton = useI18n({
    defaultMessage: 'Navigate to next image',
    id: 'stockcars.stage.button.next.arialabel',
  });
  const popupContent = useI18n({
    id: 'nemo.ui.sc.stage.renderedDisclaimer',
    defaultMessage: '',
  });

  const triggerRef = React.useRef<any>(null);

  const [index, setIndex] = useState(0);

  const [focusLayerNode, setFocusLayerNode] = React.useState<Element>();
  const [focusLayer, setFocusLayer] = React.useState<LayerHandleV1<{}>>();

  const focusLayerRef = useCallback(
    (node: any) => {
      setFocusLayerNode(node);
    },
    [setFocusLayerNode]
  );

  const openFocusLayer = () => {
    const layer = layerManager.openFocusLayer(
      () => <div ref={focusLayerRef} />,
      {},
      {
        size: FocusLayerSizeV2.A,
        userCloseable: true,
        onClose: () => {
          closeFocusLayer();
        },
      }
    );

    trackOpenGallery(trackingService, '', 'icon');
    setFocusLayer(layer);
  };

  const closeFocusLayer = () => {
    trackCloseGallery(trackingService);
    focusLayer?.close();
    setTimeout(() => {
      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    }, 50);
  };

  const totalImages = pictures.length;

  if (!totalImages) {
    return null;
  }

  // Handle next image click
  const handleNext = (isMobile: boolean = false) => {
    if (index < totalImages - 1) {
      const elementName = isMobile ? 'image' : 'arrow';
      const actionType = isMobile ? 'swipe' : 'click';
      const nextPos = index + 1;
      trackImageChange(trackingService, elementName, [index, nextPos], actionType);
      setIndex((prevIndex) => prevIndex + 1);
    }
  };

  // Handle previous image click
  const handlePrev = (isMobile: boolean = false) => {
    if (index >= 1) {
      const elementName = isMobile ? 'image' : 'arrow';
      const actionType = isMobile ? 'swipe' : 'click';
      const prevPos = index - 1;
      trackImageChange(trackingService, elementName, [index, prevPos], actionType);
      setIndex((prevIndex) => prevIndex - 1);
    }
  };

  // Handle touch swipe
  let touchStartX = 0;
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    if (touchStartX - touchEndX > 50) {
      handleNext(true);
    } else if (touchEndX - touchStartX > 50) {
      handlePrev(true);
    }
  };

  const pictureIsFallback = totalImages === 1;

  // buttons for desktop
  const iconsAndHandlers: [IconNamesType, () => void][] = [
    ['resize-increase-active', () => openFocusLayer()],
    ['back-active', handlePrev],
    ['forward-active', handleNext],
  ];

  // buttons for mobile
  const mobileIconsAndHandlers: [IconNamesType, () => void][] = [
    ['back-active', handlePrev],
    ['forward-active', handleNext],
  ];

  const shouldDisable = (currentIndex: number, icon: IconNamesType): boolean =>
    icon !== 'resize-increase-active' &&
    ((currentIndex === 0 && icon === 'back-active') ||
      (currentIndex === totalImages - 1 && icon === 'forward-active'));

  type ScreenType = 'mobile' | 'desktop';
  const renderNavIcons = (
    icon: IconNamesType,
    handler: (isMobile: boolean) => void,
    screenType: ScreenType
  ) => {
    return (
      <IconButton
        ref={icon === 'resize-increase-active' ? triggerRef : null}
        key={icon}
        icon={icon}
        data-testid={`${icon}-${screenType}`}
        disabled={shouldDisable(index, icon)}
        label=""
        onClick={() => handler(false)}
        size="sm"
        type="button"
        variant={screenType === 'desktop' ? 'secondary' : 'tertiary'}
        aria-label={
          // eslint-disable-next-line no-nested-ternary
          icon === 'back-active'
            ? ariaLabelPreviousButton
            : icon === 'forward-active'
              ? ariaLabelNextButton
              : ariaLabelExpandButton
        }
      />
    );
  };

  return (
    <>
      {focusLayerNode &&
        ReactDOM.createPortal(
          <FullScreenPicture
            vehicle={vehicle}
            handleTouchStart={handleTouchStart}
            handleTouchEnd={handleTouchEnd}
            handlePrev={handlePrev}
            handleNext={handleNext}
            pictures={pictures}
            carLineDescription={vehicle?.symbolicCarline?.description}
            imgIndex={index}
            shouldDisable={shouldDisable}
            pictureIsFallback={pictureIsFallback}
            data-testid="full-screen-picture"
          />,
          focusLayerNode
        )}
      <GalleryContainer
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        data-testid="gallery-container"
      >
        <ImageWrapper style={{ transform: `translateX(-${index * 100}%)` }}>
          {pictures.map((picture, indexValue) => (
            <SlideImage
              key={picture.key}
              picture={picture}
              carLineDescription={vehicle?.symbolicCarline?.description || ''}
              vehicle={vehicle}
              index={indexValue}
            />
          ))}
        </ImageWrapper>

        {!pictureIsFallback && (
          <ButtonContainer>
            <NavButtonContainer className="stage-media">
              <AvpWebstreamButton vehicle={vehicle} />
            </NavButtonContainer>
            <NavButtonContainer className="stage-controls">
              {iconsAndHandlers.map(([icon, handler]) => renderNavIcons(icon, handler, 'desktop'))}
            </NavButtonContainer>
          </ButtonContainer>
        )}
        {popupContent && (
          <RenderedMarkerPLPPDP
            picture={pictures[0]}
            padding={SYS_SIZE_FIXED_300}
            top={SYS_SIZE_FIXED_200}
            left={SYS_SIZE_FIXED_200}
          />
        )}
      </GalleryContainer>
      {!pictureIsFallback && (
        <MobileNavButtonContainer>
          {mobileIconsAndHandlers.map(([icon, handler]) => renderNavIcons(icon, handler, 'mobile'))}
        </MobileNavButtonContainer>
      )}
    </>
  );
};

export default SwipePicture;
